/**
 * Dentis Price Override
 *
 * Listens for `dentis:discount-resolved` and mutates `[data-dentis-price]`
 * elements in place: strikes the original price and replaces the current-price
 * slot with the Shopify-computed discounted amount.
 *
 * Three rendering "styles":
 *   - default       (snippet `price.liquid`, `topmeta-price.liquid`):
 *       .price__regular (no compare) → wrap in <s>, then add <s class="dentis-discount-applied"> after
 *       .price__sale > <s class="price-item--regular">      → leave alone (Shopify sale strike)
 *       .price-item--sale span                               → override text
 *   - bundle        ([data-dentis-price-style="bundle"]):
 *       [data-dentis-price-compare] → leave alone (existing strike) OR add if missing
 *       [data-dentis-price-current] → override text
 *   - dpc           ([data-dentis-price-style="dpc"]):
 *       [data-dentis-price-compare] → leave alone OR add if missing (mirrors bundle)
 *       [data-dentis-price-current] → override text
 *
 * Silent fallback: any DOM mutation that throws is swallowed; node restored if possible.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'dentis:discount-resolved';
  var APPLIED_CLASS = 'dentis-discount-applied';
  var ORIGINAL_INNER_ATTR = 'data-dentis-original-inner';
  var DBG = (window.DentisDebug || { enabled: false, log: function () {}, warn: function () {}, error: function () {} });

  // Capture-before-mutate so painted discounts can be reversed when the engine
  // dispatches a corrected resolved (e.g. cart-truth reconciliation discovered
  // Shopify rejected the code). Stores the price node's innerHTML once,
  // skipping re-captures if already saved.
  function captureOriginalForRestore(node) {
    if (!node || node.hasAttribute(ORIGINAL_INNER_ATTR)) return;
    try { node.setAttribute(ORIGINAL_INNER_ATTR, node.innerHTML); } catch (e) { /* swallow */ }
  }

  // Restore a previously-painted price node to its Liquid-rendered original.
  // Restores innerHTML, drops the APPLIED_CLASS + price--on-sale, and rolls
  // back any savings badges that captured their original textContent in
  // updateSavingsBadges (`data-dentis-savings-original`).
  function restoreNode(node) {
    if (!node) return;
    try {
      var snapshot = node.getAttribute(ORIGINAL_INNER_ATTR);
      if (snapshot != null) {
        node.innerHTML = snapshot;
        node.removeAttribute(ORIGINAL_INNER_ATTR);
      }
      node.classList.remove(APPLIED_CLASS);
      node.classList.remove('price--on-sale');

      // Restore badges that were painted by updateSavingsBadges.
      var scope = (node.closest && node.closest('.dentis-bundle-picker__option')) || node;
      if (scope && scope.querySelectorAll) {
        var badges = scope.querySelectorAll('[data-dentis-savings-badge]');
        for (var i = 0; i < badges.length; i++) {
          var badge = badges[i];
          if (badge.hasAttribute('data-dentis-savings-original')) {
            badge.textContent = badge.getAttribute('data-dentis-savings-original');
            badge.removeAttribute('data-dentis-savings-original');
            badge.style.display = '';
          }
        }
      }
    } catch (e) { DBG.warn('restoreNode failed', e, node); }
  }

  function restoreAll() {
    var nodes = document.querySelectorAll('[' + ORIGINAL_INNER_ATTR + '], [data-dentis-price].' + APPLIED_CLASS);
    DBG.log('restoreAll: rolling back ' + nodes.length + ' painted nodes');
    for (var i = 0; i < nodes.length; i++) restoreNode(nodes[i]);
  }

  // Format a numeric Shopify cents value to the same money pattern as Liquid's
  // `money_without_trailing_zeros` filter. We keep the locale-derived currency
  // glyph, rounding to integer kr in NO/DK/SE/FI markets and to the nearest
  // 0.05 / 0.01 elsewhere via Intl.NumberFormat fallback.
  function formatMoney(cents, sample) {
    if (cents == null || isNaN(cents)) return null;
    cents = Math.round(Number(cents) / 100) * 100;
    var amount = cents / 100;

    // Detect sample's thousands separator and decimal separator.
    function detectSeparators(str) {
      var hasComma = str.indexOf(',') > -1;
      var hasDot = str.indexOf('.') > -1;
      var hasSpace = /\s/.test(str.replace(/^\s+|\s+$/g, ''));
      // EU/Nordic: 1 299,99 or 1.299,99 → decimal=',', thousands=' ' or '.'
      // EN/US: 1,299.99 → decimal='.', thousands=','
      var decimal = '.';
      var thousands = ',';
      if (hasComma && hasDot) {
        if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
          decimal = ','; thousands = '.';
        } else {
          decimal = '.'; thousands = ',';
        }
      } else if (hasComma && !hasDot) {
        // Either Nordic decimal or English thousands. Look at digits after comma:
        // 2 digits = decimal; 3 digits = thousands.
        var afterComma = str.split(',').pop();
        if (/^\d{1,2}\b/.test(afterComma)) { decimal = ','; thousands = ' '; }
        else { decimal = '.'; thousands = ','; }
      } else if (hasDot && !hasComma) {
        var afterDot = str.split('.').pop();
        if (/^\d{1,2}\b/.test(afterDot)) { decimal = '.'; thousands = ' '; }
        else { decimal = ','; thousands = '.'; }
      } else if (hasSpace) {
        decimal = ','; thousands = ' ';
      }
      return { decimal: decimal, thousands: thousands };
    }

    function withThousands(intStr, sep) {
      return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }

    if (sample && typeof sample === 'string') {
      var match = sample.match(/(-?[\d\.\s,]+)/);
      if (match) {
        var seps = detectSeparators(match[1]);
        var unit = sample.replace(match[1], '').replace(/\s+/g, ' ').trim();
        var rendered;
        if (Math.abs(amount - Math.round(amount)) < 0.005) {
          rendered = withThousands(String(Math.round(amount)), seps.thousands);
        } else {
          var parts = amount.toFixed(2).split('.');
          rendered = withThousands(parts[0], seps.thousands) + seps.decimal + parts[1];
        }
        if (!unit) return rendered;
        if (sample.trim().indexOf(unit) === 0) return unit + ' ' + rendered;
        return rendered + ' ' + unit;
      }
    }

    // Fallback: integer kr (works for NO/DK/SE/FI/NL/DE).
    if (Math.abs(amount - Math.round(amount)) < 0.005) {
      return withThousands(String(Math.round(amount)), ' ') + ' kr';
    }
    return amount.toFixed(2).replace('.', ',') + ' kr';
  }

  // Match a single sub-resolved entry against a variant. Returns the discount
  // info {perUnitSavingsCents, type, value} when the variant is eligible, null
  // when not. Used both for the top-level resolved and for each sub-resolved
  // in resolved.discounts.
  function matchSingleDiscountForVariant(subResolved, pid, vid) {
    if (!subResolved || !subResolved.active) return null;
    // display_covered discounts (e.g. an Eevy bundle campaign) already have
    // their savings reflected in the displayed price by another renderer (the
    // bundle picker repaints to the campaign offer price). Painting them here
    // would double-discount, so never produce a price change for them — the
    // code + % still surface in the announcement via promo-status.
    if (subResolved.display_covered === true) return null;
    if (subResolved.type !== 'percentage' && subResolved.type !== 'fixed') return null;
    var bucket = subResolved.eligible && subResolved.eligible[pid];
    if (bucket) {
      var perVariant = bucket.variants && bucket.variants[vid];
      if (perVariant != null) {
        return {
          perUnitSavingsCents: Number(perVariant),
          type: subResolved.type,
          value: Number(subResolved.value || 0)
        };
      }
      if (bucket.restrict_to_variants) {
        return null;
      }
      return {
        perUnitSavingsCents: 0,
        type: subResolved.type,
        value: Number(subResolved.value || 0)
      };
    }
    if (subResolved.target_selection === 'all' && (subResolved.target_type === 'line_item' || !subResolved.target_type)) {
      return {
        perUnitSavingsCents: 0,
        type: subResolved.type,
        value: Number(subResolved.value || 0)
      };
    }
    return null;
  }

  function computeDiscountedPriceCents(basePriceCents, info) {
    if (!info) return null;
    if (info.perUnitSavingsCents > 0) {
      return Math.max(0, basePriceCents - info.perUnitSavingsCents);
    }
    // Defensive fallback: derive from value/value_type
    if (info.type === 'percentage' && info.value > 0) {
      return Math.max(0, Math.round(basePriceCents * (1 - info.value / 100)));
    }
    if (info.type === 'fixed' && info.value > 0) {
      // value is in shop currency units (kr); convert to cents
      return Math.max(0, basePriceCents - Math.round(info.value * 100));
    }
    return null;
  }

  // ────────────────────────────────────────────────────────────────────────
  // Per-variant composer
  //
  // Given a resolved discount payload (single or multi via resolved.discounts)
  // and a variant's base price, returns the final composed discounted price
  // by applying Shopify's combinability matrix:
  //
  //   1. Same-kind group resolution (product vs product, order vs order):
  //        - All combines[kind]=true ("permissive") apply.
  //        - Among combines[kind]=false ("exclusive"), only the biggest-
  //          savings one applies; the rest are dropped.
  //
  //   2. Cross-kind compatibility (between the surviving product set and
  //      the order discount, if any):
  //        - All product survivors must have combines.order=true.
  //        - The order discount must have combines.product=true.
  //        - Otherwise the order discount is dropped.
  //
  //   3. Sequential application: percent discounts apply first
  //      (multiplicatively), then fixed-amount discounts (subtractively).
  //      Within each tier, deterministic order doesn't change the math
  //      (percents multiply commutatively, fixed sums add commutatively).
  //
  // Returns { discountedCents, applied[], dropped[], details } for
  // composability with the existing paint pipeline. Returns null when the
  // variant has no eligible discounts.
  // ────────────────────────────────────────────────────────────────────────
  function kindOfSub(sub) {
    return (sub && sub.kind === 'order') ? 'order' : 'product';
  }
  function combinesOfSub(sub) {
    var src = (sub && sub.combines) || {};
    return {
      order: src.order !== false,
      product: src.product !== false
    };
  }
  function savingsAgainstBase(basePriceCents, match) {
    var dp = computeDiscountedPriceCents(basePriceCents, match);
    if (dp == null) return 0;
    return Math.max(0, basePriceCents - dp);
  }

  // `opts.orderOnly` restricts composition to order-kind discounts only,
  // dropping product/line-kind discounts. The cart drawer uses this for line
  // items: Shopify already allocates line-level discounts into
  // `item.final_line_price`, so only the order-level portion (e.g. an
  // influencer "20% off order" code) is missing from the per-line display.
  // Painting order-only avoids double-counting the line-level discounts.
  function composeDiscountForVariant(resolved, basePriceCents, productId, variantId, opts) {
    if (!resolved || !resolved.active || !basePriceCents) return null;
    var orderOnly = !!(opts && opts.orderOnly);
    var pid = String(productId || '');
    var vid = String(variantId || '');
    var subs = Array.isArray(resolved.discounts) && resolved.discounts.length > 0
      ? resolved.discounts
      : [resolved];

    var products = [];
    var orders = [];
    for (var i = 0; i < subs.length; i++) {
      var sub = subs[i];
      var match = matchSingleDiscountForVariant(sub, pid, vid);
      if (!match) continue;
      var entry = {
        sub: sub,
        match: match,
        combines: combinesOfSub(sub),
        kind: kindOfSub(sub),
        savingsCents: savingsAgainstBase(basePriceCents, match)
      };
      if (entry.kind === 'product') {
        if (orderOnly) continue;
        products.push(entry);
      } else if (entry.kind === 'order') orders.push(entry);
    }

    if (products.length === 0 && orders.length === 0) return null;

    // Same-kind product resolution: keep all permissive, pick biggest exclusive.
    var permissiveProducts = [];
    var exclusiveProducts = [];
    for (var p = 0; p < products.length; p++) {
      if (products[p].combines.product) permissiveProducts.push(products[p]);
      else exclusiveProducts.push(products[p]);
    }
    var exclusiveWinner = null;
    var droppedExclusives = [];
    for (var e = 0; e < exclusiveProducts.length; e++) {
      var cand = exclusiveProducts[e];
      if (!exclusiveWinner || cand.savingsCents > exclusiveWinner.savingsCents) {
        if (exclusiveWinner) droppedExclusives.push(exclusiveWinner);
        exclusiveWinner = cand;
      } else {
        droppedExclusives.push(cand);
      }
    }
    var activeProducts = permissiveProducts.slice();
    if (exclusiveWinner) activeProducts.push(exclusiveWinner);

    // Cross-kind: at most one order discount expected (the storewide single).
    // If any active product refuses to combine with orders, OR the order
    // refuses to combine with products, drop the order discount.
    var activeOrder = null;
    var droppedOrder = null;
    if (orders.length > 0) {
      var orderEntry = orders[0];
      var anyProductBlocksOrder = false;
      for (var ap = 0; ap < activeProducts.length; ap++) {
        if (!activeProducts[ap].combines.order) { anyProductBlocksOrder = true; break; }
      }
      var orderRefusesProduct = !orderEntry.combines.product;
      if (anyProductBlocksOrder || orderRefusesProduct) {
        droppedOrder = orderEntry;
      } else {
        activeOrder = orderEntry;
      }
    }

    // Sequential application. Product-kind discounts apply first (per-line
    // allocation), then order-kind discounts compose on the post-product
    // subtotal — matches Shopify's checkout allocation order so a fixed
    // product-level deal (e.g. DUO-PAKKEPRIS 901 kr off) gets taken off the
    // line BEFORE a percentage order-level code (e.g. LINE20 20% off) is
    // applied to what remains. Within each kind, insertion order is stable.
    var result = basePriceCents;
    var applied = [];
    var pending = activeProducts.slice();
    if (activeOrder) pending.push(activeOrder);
    pending.sort(function (a, b) {
      var aOrder = a.kind === 'order' ? 1 : 0;
      var bOrder = b.kind === 'order' ? 1 : 0;
      return aOrder - bOrder;
    });
    for (var pi = 0; pi < pending.length; pi++) {
      var step = pending[pi];
      var nextCents = computeDiscountedPriceCents(result, step.match);
      if (nextCents != null && nextCents < result) {
        result = nextCents;
        applied.push(step);
      }
    }

    if (result === basePriceCents) return null;
    return {
      discountedCents: result,
      savingsCents: basePriceCents - result,
      applied: applied,
      dropped_exclusive_products: droppedExclusives,
      dropped_order: droppedOrder
    };
  }

  // Legacy adapter: callers that still want a single info-object (used by
  // PAINT-NODE trace logging) get a synthesized one summarizing the
  // composition.
  function getDiscountForVariant(resolved, productId, variantId, basePriceCents) {
    var composed = composeDiscountForVariant(resolved, basePriceCents, productId, variantId);
    if (!composed) return null;
    // Synthesize a info-style result for backwards compat with the paint
    // styles (they consume `discounted` directly, but the trace logging
    // wants type/value for visibility).
    return {
      perUnitSavingsCents: composed.savingsCents,
      type: composed.applied.length === 1 ? composed.applied[0].match.type : 'composed',
      value: composed.applied.length === 1 ? composed.applied[0].match.value : 0,
      _composed: composed
    };
  }

  // Update Shopify's `<span class="price__badge-sale">Spar X%</span>` so the
  // percent shown reflects the FULL savings from compare_at to the painted
  // discount price (not the original compare_at -> sale percent that Liquid
  // baked in). Leaving the old "Spar 31%" badge in place when the painted
  // price is now 719 kr creates the visual contradiction the user calls
  // "double-applying" — the strikethrough shows 2798 kr, the current shows
  // 719 kr, but the badge says only 31% off. Updating the badge to
  // "Spar 74%" makes the math reconcile.
  function updateSaleBadge(node, comparePriceCents, basePriceCents, discountedCents) {
    if (!node || !node.querySelector) return;
    var badge = node.querySelector('.price__badge-sale, .badge.price__badge-sale');
    if (!badge) return;
    if (!badge.hasAttribute('data-dentis-badge-original')) {
      badge.setAttribute('data-dentis-badge-original', badge.textContent);
    }
    var effectiveCompare = comparePriceCents > basePriceCents ? comparePriceCents : basePriceCents;
    if (effectiveCompare <= 0 || discountedCents == null || discountedCents >= effectiveCompare) {
      badge.style.display = 'none';
      return;
    }
    var percent = Math.round((effectiveCompare - discountedCents) / effectiveCompare * 100);
    if (percent <= 0) {
      badge.style.display = 'none';
      return;
    }
    var origText = badge.textContent;
    var updated = origText.replace(/-?\d+(?:[.,]\d+)?\s*%/, percent + '%');
    if (updated === origText) {
      // Fallback if the original text didn't contain a percent token — some
      // markets format differently. Rewrite conservatively.
      updated = 'Spar ' + percent + '%';
    }
    badge.textContent = updated;
    badge.style.display = '';
  }

  function applyDefaultStyle(node, basePriceCents, comparePriceCents, discountedCents) {
    captureOriginalForRestore(node);
    var saleEl = node.querySelector('.price__sale');
    var regularEl = node.querySelector('.price__regular');
    var saleItem = node.querySelector('.price-item--sale');
    var sample = (saleItem && saleItem.textContent.trim())
                 || (regularEl && regularEl.querySelector('.price-item--regular') && regularEl.querySelector('.price-item--regular').textContent.trim())
                 || '';
    var formatted = formatMoney(discountedCents, sample);
    if (!formatted) return;

    if (comparePriceCents > basePriceCents) {
      // Sale case — strike original (compare_at) is already there. Just override the sale text.
      if (saleItem) saleItem.textContent = formatted;
    } else {
      // Non-sale case — convert the regular display into a strike + discounted line.
      if (regularEl) {
        var inner = regularEl.querySelector('.price-item--regular');
        if (inner) {
          inner.innerHTML = '<s class="price-item price-item--regular price-item--regular-was">' + inner.textContent.trim() + '</s>'
            + ' <span class="price-item price-item--sale price-item--last ' + APPLIED_CLASS + '">' + formatted + '</span>';
        }
      }
      if (saleItem) saleItem.textContent = formatted;
    }
    updateSaleBadge(node, comparePriceCents, basePriceCents, discountedCents);
    node.classList.add(APPLIED_CLASS);
    node.classList.add('price--on-sale');
  }

  function applyBundleStyle(node, basePriceCents, comparePriceCents, discountedCents) {
    captureOriginalForRestore(node);
    var current = node.querySelector('[data-dentis-price-current]');
    var compare = node.querySelector('[data-dentis-price-compare]');
    var sample = (current && current.textContent.trim()) || '';
    var formatted = formatMoney(discountedCents, sample);
    if (!formatted) return;
    if (current) current.textContent = formatted;

    // Ensure original price is shown as strike (use compare_at if present, else base)
    var strikeBaseCents = comparePriceCents > basePriceCents ? comparePriceCents : basePriceCents;
    var strikeFormatted = formatMoney(strikeBaseCents, sample);
    if (!compare && strikeFormatted) {
      compare = document.createElement('span');
      // Derive the compare-original class from the current-price class so the
      // strikethrough CSS for whichever picker is on the page (bundle picker
      // OR image-grid picker) actually applies. Hardcoding the bundle-picker
      // class lost the line-through on image-grid PDPs like refill-pakken-duo
      // where the bundle-picker CSS isn't loaded.
      var currentClass = (current && current.className) || '';
      var originalClass = currentClass.indexOf('dentis-image-grid-picker') !== -1
        ? 'dentis-image-grid-picker__price-original'
        : 'dentis-bundle-picker__price-original';
      compare.className = originalClass;
      compare.setAttribute('data-dentis-price-compare', '');
      compare.textContent = strikeFormatted;
      if (current && current.parentNode) current.parentNode.insertBefore(compare, current);
    } else if (compare && comparePriceCents <= basePriceCents) {
      // No native sale, base becomes strike
      compare.textContent = strikeFormatted;
    }

    var bundleScope = (node.closest && node.closest('.dentis-bundle-picker__option')) || node;
    updateSavingsBadges(bundleScope, discountedCents);

    node.classList.add(APPLIED_CLASS);
  }

  function updateSavingsBadges(scope, discountedCents) {
    if (!scope || !scope.querySelectorAll) return;
    var badges = scope.querySelectorAll('[data-dentis-savings-badge]');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      var baseCents = parseInt(badge.getAttribute('data-dentis-savings-base-cents') || '0', 10);
      var compareCents = parseInt(badge.getAttribute('data-dentis-savings-compare-cents') || '0', 10);
      var format = badge.getAttribute('data-dentis-savings-format') || '-{percent}%';

      if (!badge.hasAttribute('data-dentis-savings-original')) {
        badge.setAttribute('data-dentis-savings-original', badge.textContent);
      }

      var effectiveCompare = compareCents > baseCents ? compareCents : baseCents;
      if (effectiveCompare <= 0 || discountedCents == null || discountedCents >= effectiveCompare) {
        badge.style.display = 'none';
        continue;
      }

      var percent = Math.round((effectiveCompare - discountedCents) / effectiveCompare * 100);
      if (percent <= 0) {
        badge.style.display = 'none';
        continue;
      }

      badge.textContent = format.replace('{percent}', String(percent));
      badge.style.display = '';
    }
  }

  function applyDpcStyle(node, basePriceCents, comparePriceCents, discountedCents) {
    captureOriginalForRestore(node);
    var current = node.querySelector('[data-dentis-price-current]');
    var compare = node.querySelector('[data-dentis-price-compare]');
    var sample = (current && current.textContent.trim()) || '';
    var formatted = formatMoney(discountedCents, sample);
    if (!formatted) return;
    if (current) current.textContent = formatted;

    var strikeBaseCents = comparePriceCents > basePriceCents ? comparePriceCents : basePriceCents;
    var strikeFormatted = formatMoney(strikeBaseCents, sample);
    if (!compare && strikeFormatted) {
      compare = document.createElement('span');
      compare.className = 'dpc-flex__value-secondary';
      compare.setAttribute('data-dentis-price-compare', '');
      compare.textContent = strikeFormatted;
      if (current && current.parentNode) current.parentNode.insertBefore(compare, current);
    } else if (compare && comparePriceCents <= basePriceCents) {
      compare.textContent = strikeFormatted;
    }
    node.classList.add(APPLIED_CLASS);
  }

  // Each paint cycle gets a monotonic id so multi-event sequences are easy to
  // correlate in the console (e.g., "PAINT-START #3" -> "PAINT-NODE #3 ..." ->
  // "PAINT-END #3"). Re-paints from a subsequent dispatch get #4, etc.
  var PAINT_CYCLE = 0;
  function pt(phase, msg, payload) {
    if (DBG.t) DBG.t(phase, msg, payload);
    else if (DBG.log) DBG.log('[' + phase + ']', msg, payload);
  }
  function ptw(phase, msg, payload) {
    if (DBG.tw) DBG.tw(phase, msg, payload);
    else if (DBG.warn) DBG.warn('[' + phase + ']', msg, payload);
  }

  // Lightweight node identity for log messages — useful when scanning the
  // console for "which DOM element is this?".
  function nodeRef(node) {
    if (!node) return null;
    var tag = node.tagName ? node.tagName.toLowerCase() : '?';
    var id = node.id ? '#' + node.id : '';
    var cls = node.className && typeof node.className === 'string'
      ? '.' + node.className.split(/\s+/).filter(Boolean).slice(0, 3).join('.')
      : '';
    return tag + id + cls;
  }

  function processNode(node, resolved, paintCycle) {
    if (!node) return { status: 'no-node' };
    if (node.classList.contains(APPLIED_CLASS)) {
      // CRITICAL: double-paint detection. If a node is already painted and
      // we're processing it again WITHOUT restoreAll, that's a bug — the
      // caller should have restored first. Log it loudly.
      ptw('PAINT-DUP', 'node already has ' + APPLIED_CLASS + '; skipping (likely double-paint attempt)', {
        cycle: paintCycle,
        node: nodeRef(node),
        productId: node.getAttribute('data-dentis-product-id'),
        variantId: node.getAttribute('data-dentis-variant-id')
      });
      return { status: 'already-painted' };
    }
    var basePrice = parseInt(node.getAttribute('data-dentis-base-price') || '0', 10);
    var comparePrice = parseInt(node.getAttribute('data-dentis-compare-price') || '0', 10);
    var productId = node.getAttribute('data-dentis-product-id');
    var variantId = node.getAttribute('data-dentis-variant-id');
    var style = node.getAttribute('data-dentis-price-style') || 'default';
    var scope = node.getAttribute('data-dentis-price-scope') || '';
    if (!basePrice || !productId) {
      ptw('PAINT-NODE', 'skipped (missing basePrice or productId)', { cycle: paintCycle, node: nodeRef(node), basePrice: basePrice, productId: productId });
      return { status: 'missing-data' };
    }

    var composed = composeDiscountForVariant(resolved, basePrice, productId, variantId, { orderOnly: scope === 'order' });
    if (!composed) {
      pt('PAINT-NODE', 'not eligible -> untouched', {
        cycle: paintCycle,
        node: nodeRef(node),
        productId: productId,
        variantId: variantId,
        style: style,
        basePrice: basePrice,
        resolved_discounts: Array.isArray(resolved.discounts) ? resolved.discounts.length : 1
      });
      return { status: 'not-eligible' };
    }
    var discounted = composed.discountedCents;
    if (discounted == null || discounted === basePrice) {
      ptw('PAINT-NODE', 'discount produces no price change -> untouched', {
        cycle: paintCycle,
        node: nodeRef(node),
        productId: productId,
        variantId: variantId,
        style: style,
        basePrice: basePrice,
        discounted: discounted,
        composed: composed
      });
      return { status: 'no-change' };
    }

    if (composed.dropped_exclusive_products && composed.dropped_exclusive_products.length > 0) {
      pt('COMPOSE', 'exclusive product discount(s) dropped (bigger wins)', {
        winner: (function () {
          var w = null;
          for (var ai = 0; ai < composed.applied.length; ai++) {
            if (!composed.applied[ai].combines.product) { w = composed.applied[ai]; break; }
          }
          return w ? { code: w.sub.code, saving_cents: w.savingsCents } : null;
        })(),
        dropped: composed.dropped_exclusive_products.map(function (d) {
          return { code: d.sub.code, saving_cents: d.savingsCents };
        })
      });
    }
    if (composed.dropped_order) {
      pt('COMPOSE', 'order discount dropped (cross-kind block)', {
        order_code: composed.dropped_order.sub.code,
        reason: 'product-side refuses combines.order OR order-side refuses combines.product'
      });
    }

    try {
      if (style === 'bundle') {
        applyBundleStyle(node, basePrice, comparePrice, discounted);
      } else if (style === 'dpc') {
        applyDpcStyle(node, basePrice, comparePrice, discounted);
      } else {
        applyDefaultStyle(node, basePrice, comparePrice, discounted);
      }
      pt('PAINT-NODE', 'painted ' + (basePrice / 100) + ' -> ' + (discounted / 100), {
        cycle: paintCycle,
        node: nodeRef(node),
        productId: productId,
        variantId: variantId,
        style: style,
        basePrice: basePrice,
        discounted: discounted,
        saved: basePrice - discounted,
        applied_codes: composed.applied.map(function (a) { return a.sub.code + '(' + a.match.type + ':' + a.match.value + ')'; }),
        applied_kinds: composed.applied.map(function (a) { return a.kind; })
      });
      return { status: 'painted', saved: basePrice - discounted, variantId: variantId };
    } catch (e) {
      DBG.error('PAINT-NODE apply style failed; node left untouched', e, node);
      return { status: 'error' };
    }
  }

  // Guard flag set true while processAll / processNode is actively mutating
  // the DOM. Lets the MutationObserver ignore our own writes so we don't
  // recurse into an infinite paint loop on every textContent assignment.
  var PAINT_IN_PROGRESS = false;

  function processAll(resolved) {
    PAINT_CYCLE++;
    var cycle = PAINT_CYCLE;

    // Active iff (a) resolved.active and either (b1) top-level type is
    // a paintable kind OR (b2) at least one sub-discount is paintable.
    // Multi-discount resolveds (cores + primary) may carry non-paintable
    // metadata at the top level when synthesized.
    var coreActive = !!(resolved && resolved.active);
    var typeActive = coreActive && (resolved.type === 'percentage' || resolved.type === 'fixed');
    if (!typeActive && coreActive && Array.isArray(resolved.discounts)) {
      for (var di = 0; di < resolved.discounts.length; di++) {
        var sd = resolved.discounts[di];
        if (sd && sd.active && (sd.type === 'percentage' || sd.type === 'fixed')) { typeActive = true; break; }
      }
    }
    if (!coreActive || !typeActive) {
      pt('PAINT-START', '#' + cycle + ' inactive -> restoreAll', { resolved_active: coreActive, source: resolved && resolved.source });
      PAINT_IN_PROGRESS = true;
      try { restoreAll(); } finally { PAINT_IN_PROGRESS = false; }
      pt('PAINT-END', '#' + cycle + ' restore complete');
      return;
    }

    var alreadyPaintedBefore = document.querySelectorAll('[data-dentis-price].' + APPLIED_CLASS).length;
    pt('PAINT-START', '#' + cycle + ' active dispatch', {
      code: resolved.code,
      type: resolved.type,
      value: resolved.value,
      source: resolved.source || null,
      discount_count: Array.isArray(resolved.discounts) ? resolved.discounts.length : 1,
      already_painted_nodes: alreadyPaintedBefore
    });

    PAINT_IN_PROGRESS = true;
    try {
      if (alreadyPaintedBefore > 0) {
        pt('PAINT-START', '#' + cycle + ' restoring ' + alreadyPaintedBefore + ' previously painted nodes before re-paint');
        restoreAll();
      }
      var nodes = document.querySelectorAll('[data-dentis-price]');

      var summary = { total: nodes.length, painted: 0, not_eligible: 0, no_change: 0, already_painted: 0, errors: 0, savings: [] };
      for (var i = 0; i < nodes.length; i++) {
        var result = processNode(nodes[i], resolved, cycle);
        if (result.status === 'painted') {
          summary.painted++;
          summary.savings.push({ variant: result.variantId, saved: result.saved });
        } else if (result.status === 'not-eligible') summary.not_eligible++;
        else if (result.status === 'no-change') summary.no_change++;
        else if (result.status === 'already-painted') summary.already_painted++;
        else if (result.status === 'error') summary.errors++;
      }
      pt('PAINT-END', '#' + cycle + ' summary', summary);
    } finally {
      PAINT_IN_PROGRESS = false;
    }
  }

  // Returns the nearest ancestor (or self) that matches [data-dentis-price],
  // or null. Used by the observer to identify whether a mutation happened
  // inside one of our paint targets.
  function ancestorPaintNode(node) {
    var hop = node && node.nodeType === 1 ? node : (node && node.parentNode);
    while (hop && hop !== document.body) {
      if (hop.nodeType === 1 && hop.hasAttribute && hop.hasAttribute('data-dentis-price')) return hop;
      hop = hop.parentNode;
    }
    return null;
  }

  var BOUND_OBSERVER = false;
  var REPAINT_TIMER = null;
  function scheduleRepaint(reason, resolved) {
    if (REPAINT_TIMER) return; // already queued; coalesce
    REPAINT_TIMER = setTimeout(function () {
      REPAINT_TIMER = null;
      var current = window.DentisActiveDiscount || resolved;
      if (!current || !current.active) return;
      pt('OBSERVER', 'repaint scheduled by ' + reason);
      processAll(current);
    }, 50);
  }

  function bindMutationObserver(resolved) {
    if (!('MutationObserver' in window)) return;
    if (!resolved || !resolved.active) return;
    if (BOUND_OBSERVER) {
      pt('OBSERVER', 'already bound; skipping re-bind (would have caused duplicate paints)');
      return;
    }
    BOUND_OBSERVER = true;
    pt('OBSERVER', 'binding for active resolved', { code: resolved.code });
    var observer = new MutationObserver(function (mutations) {
      if (PAINT_IN_PROGRESS) return; // ignore our own writes
      var observerCycle = '-obs-' + PAINT_CYCLE;
      var externalPaintNodeTouched = false;
      for (var m = 0; m < mutations.length; m++) {
        var mut = mutations[m];

        // Branch 1: New [data-dentis-price] nodes (e.g. variant-swap
        // re-renders, lazy-loaded sections). Paint them in place.
        if (mut.addedNodes && mut.addedNodes.length > 0) {
          for (var n = 0; n < mut.addedNodes.length; n++) {
            var node = mut.addedNodes[n];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-dentis-price]')) {
              processNode(node, window.DentisActiveDiscount || resolved, observerCycle);
            }
            if (node.querySelectorAll) {
              var inner = node.querySelectorAll('[data-dentis-price]');
              for (var i = 0; i < inner.length; i++) processNode(inner[i], window.DentisActiveDiscount || resolved, observerCycle);
            }
          }
        }

        // Branch 2: Text changes (characterData) inside an already-painted
        // node — third-party theme scripts overwriting our paint. Mark for
        // debounced re-paint at the end of the mutation batch.
        if (mut.type === 'characterData' || (mut.type === 'childList' && mut.addedNodes && mut.addedNodes.length === 0 && mut.removedNodes && mut.removedNodes.length > 0)) {
          var enclosing = ancestorPaintNode(mut.target);
          if (enclosing && enclosing.classList.contains(APPLIED_CLASS)) {
            externalPaintNodeTouched = true;
          }
        }
      }
      if (externalPaintNodeTouched) scheduleRepaint('external text overwrite on painted node', resolved);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  function handle(resolved) {
    try {
      processAll(resolved);
      bindMutationObserver(resolved);
    } catch (e) { DBG.error('handle() threw', e); }
  }

  // Public surface for promo-status (and anything else) to reuse the
  // per-variant matrix composer. Returns `null` when no sub applies at the
  // current variant, otherwise `{ discountedCents, savingsCents, applied,
  // dropped_exclusive_products, dropped_order }`.
  window.DentisPriceOverride = window.DentisPriceOverride || {};
  window.DentisPriceOverride.compose = function (resolved, basePriceCents, productId, variantId, opts) {
    try {
      return composeDiscountForVariant(resolved, basePriceCents, productId, variantId, opts);
    } catch (e) {
      DBG.warn('public compose() threw', e);
      return null;
    }
  };

  document.addEventListener(EVENT_NAME, function (ev) {
    pt('EVENT', 'received ' + EVENT_NAME, { has_detail: !!(ev && ev.detail) });
    handle((ev && ev.detail) || window.DentisActiveDiscount);
  });

  // Defensive: if the engine dispatched before this listener attached
  // (cache-hit synchronous path), pick up the already-set state on next tick.
  function maybeReplay() {
    if (window.DentisActiveDiscount) {
      pt('REPLAY', 'window.DentisActiveDiscount set before listener attach', { code: window.DentisActiveDiscount.code });
      handle(window.DentisActiveDiscount);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeReplay);
  } else {
    setTimeout(maybeReplay, 0);
  }
})();
